/**
 * `<SelectModelChip>` — control-only discovery affordance chip.
 *
 * Multi-Add Phase 2 (PRD §2.9 — Hardening). Rendered inline in
 * the discovery `<Table>` for rows that have a `control` aspect but NO
 * `video` aspect (control-only ONVIF discoveries). Surfaces a "Select
 * model" affordance so the user can pick a registry model for the
 * device before the bulk-add commit — that's what unlocks the URX +
 * brand/model resolution downstream in `buildBulkCandidate`.
 *
 * Per `reference-rules-in-comments` (ai.knowledge/global). Variant
 * extension contract (§2.9):
 *
 *   - NEW chip variant (4th color) — neutral/blue, distinct icon.
 *     Not a failure state; a UX affordance.
 *   - Distinct from W3's `<Chip>` failed-tile chips (red/yellow) so
 *     colorblind + low-contrast users can tell the affordance apart
 *     from the degraded states.
 *
 * Per `ui-architecture` (ai.knowledge/global): all colors via design-
 * system theme tokens (`text-primary`, `bg-base-100/90`) so the Hive
 * whitelabel variant inherits its own brand colors. Icon resolves via
 * the existing `<Icon>` design-system component using the canonical
 * `settings` glyph (icons.css `\e854`) — the closest semantic match
 * to "configure / pick model" in the iconfont.
 *
 * Per `think-in-systems` (ai.knowledge/global): kept as a separate
 * component rather than a branch in W3's `<Chip>` because the
 * "Select model" affordance is UX-conceptually distinct from the
 * 3-bucket state chips (which represent device-health verdicts, not
 * user actions). This split also keeps W3's chip-variant.ts type
 * extension additive without forcing changes to chip.tsx.
 *
 * Click contract: the caller wires `onClick` to open the device
 * settings modal at the "Select model" section. The chip is keyboard-
 * actionable via native `<button>` semantics (Enter / Space activate)
 * with click stopPropagation so a chip click inside a row doesn't
 * bubble into the row's selection toggle. Mirrors W3's chip-click
 * pattern for consistency. Per audit C4 (2026-05-13).
 */
import type { MouseEvent, ReactElement } from 'react';
import { Icon } from '@studio/design-system/icon';
import { twMerge } from 'cn';

export interface SelectModelChipProps {
  /**
   * Click handler — typically opens the device settings modal at the
   * "Select model" section. Optional: when omitted the chip renders
   * as a passive `<span>` (no button semantics).
   *
   * Per `exactOptionalPropertyTypes` (tsconfig) — explicit `| undefined`
   * lets callers pass a possibly-undefined expression directly.
   */
  onClick?: (() => void) | undefined;
  /** Optional CSS overrides for positioning at the call site. */
  className?: string | undefined;
  /**
   * Visible label override. Defaults to "Select model". Surface kept
   * as a prop for product/copy iteration without re-shaping the
   * component.
   */
  label?: string | undefined;
}

/**
 * Render the Select-model affordance chip. Neutral/blue color + the
 * canonical `settings` icon — distinct from W3's red/yellow state
 * chips so the user reads "this is something to configure" rather
 * than "this failed".
 *
 * Per audit C4 (2026-05-13): renders as native `<button>` when an
 * onClick is supplied (no `<div role='button' tabIndex={0}>` pattern).
 * Native button semantics give us Enter + Space activation, focus ring,
 * and screen-reader announcement for free.
 */
export const SelectModelChip = ({
  onClick,
  className,
  label = 'Select model'
}: SelectModelChipProps): ReactElement => {
  // Click stop-propagation matches W3's chip.tsx click contract so a
  // chip click inside a table row doesn't bubble into the row's
  // selection-toggle handler. Native `<button>` fires click on Enter +
  // Space, so this one handler covers mouse + keyboard activation.
  const handleClick = (event: MouseEvent<HTMLButtonElement>): void => {
    event.stopPropagation();
    onClick?.();
  };

  const sharedClassName = twMerge(
    // Pill shape with icon + text — same chrome as W3's <Chip>
    // so the chips read as a single visual family.
    'inline-flex items-center gap-4 rounded px-8 py-4 text-sm',
    // Neutral/blue affordance background + text (§2.9) — fourth color
    // distinct from the red/yellow state buckets.
    // `text-primary` resolves to the active theme's primary color
    // (Iris blue / Hive brand) so the whitelabel applies its own
    // affordance hue without forking the component.
    'bg-base-100/90 text-primary',
    onClick && 'cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-primary',
    className
  );

  const inner = (
    <>
      <Icon
        // Decorative — the aria-label on the chip already carries the
        // verbal content. Hidden from screen readers to avoid double-
        // announcement.
        aria-hidden='true'
        // `settings` glyph — semantic "configure / pick model" hint
        // and structurally distinct from W3's alert-circle / alert-
        // triangle / close-circle outlines (gear vs alert shapes).
        icon='settings'
        className='text-[14px]'
      />
      <span className='font-medium'>{label}</span>
    </>
  );

  // Per audit C4 (2026-05-13): native `<button>` instead of
  // `<div role="button" tabIndex={0}>`. Native semantics for free.
  if (onClick) {
    return (
      <button
        type='button'
        aria-label={label}
        onClick={handleClick}
        className={sharedClassName}
        data-testid='select-model-chip'
      >
        {inner}
      </button>
    );
  }

  return (
    <span aria-label={label} className={sharedClassName} data-testid='select-model-chip'>
      {inner}
    </span>
  );
};
